import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import ErrorBoundary from './components/common/ErrorBoundary';
import { UpdatePrompt } from './components/UpdatePrompt';
import Login from './pages/Login';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import Dashboard from './pages/Dashboard';
import BillingPage from './pages/BillingPage';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import { TrialBanner } from './components/subscription';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-orange-hover animate-pulse" />
          <div className="text-text-tertiary text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-orange-hover animate-pulse" />
          <div className="text-text-tertiary text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent() {
  const { isNewUser, user, userProfile } = useAuth();

  // Show onboarding if new user OR if user hasn't completed/skipped onboarding
  const needsOnboarding = isNewUser || (
    userProfile &&
    !userProfile.onboarding?.completedAt &&
    !userProfile.onboarding?.skippedAt
  );

  return (
    <>
      {/* Onboarding wizard for new or returning users who haven't completed setup */}
      {user && needsOnboarding && <OnboardingWizard />}

      {/* Trial banner for users on trial */}
      {user && <TrialBanner />}

      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignUpPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <BillingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="noise-texture">
          <div className="ambient-glow" />
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--surface-card)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
              },
              success: {
                iconTheme: {
                  primary: '#4CAF50',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#F44336',
                  secondary: '#fff',
                },
                duration: 5000,
              },
            }}
          />
          <UpdatePrompt />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}
